import styled from "@emotion/styled"
import { useScroll } from "framer-motion"
import { useRef } from "react"

import { useConsole } from "contexts/Console"
import { useViewport } from "contexts/Viewport"

import { getGradient } from "components/articles/common"
import Image from "components/media/ImageCLD"

import useSSR from "hooks/useSSR"

import BlockCTA from "./BlockCTA"
import Overlay from "./Overlay"
import PageTitleBlock from "./PageTitleBlock"
import Skyline from "./Skyline"
import SubHeading from "./SubHeading"
import { sectionStyle } from "./style"

const Section = styled.section`
  ${sectionStyle}
`

const Figure = styled.figure`
  display: block;
  position: absolute;
  width: 100%;
  height: 100%;
  grid-column: doc;
  grid-row: fullh;
  z-index: -1;

  & img {
    width: 100%;
    height: 100%;
    object-position: center;
    object-fit: cover;
    opacity: 0;
    transition: opacity 300ms;
  }
`

export default function CoverIntroImage(props) {
  const {
    heading,
    image_cld: image,
    intro,
    video_amp_cta: video_cta,
    sub_heading,
    title_gradient,
    chapo_gradient,
    className,
    theme = "dark-theme",
    secundary_theme,
    no_overlay,
    extend_height,
    increase_heading_width,
  } = props

  const console = useConsole()

  const ssr = useSSR()
  const { isMobile } = useViewport()

  const blocks = intro?.reduce((acc, item) => {
    for (const [key, value] of Object.entries(item)) {
      acc.set(key, value)
    }
    return acc
  }, new Map())

  const ref = useRef()

  const { scrollY } = useScroll({ target: ref, offset: ["start start", "end start"] })

  const ttlGradient = title_gradient?.colors ? getGradient(title_gradient.colors) : null
  const chapoGradient = chapo_gradient?.colors ? getGradient(chapo_gradient.colors) : null

  const doubled = !ssr && !isMobile.get() ? sub_heading?.length || extend_height : false

  const styles = {
    ...(ttlGradient && { "--title-gradient": ttlGradient }),
    ...(chapoGradient && { "--chapo-gradient": chapoGradient }),
    "--row-h": doubled ? "150vh" : "100vh",
  }

  return (
    <>
      <PageTitleBlock
        {...heading}
        videoCTA={video_cta[0]}
        prog={scrollY}
        theme={theme ? theme?.theme : "dark-theme"}
        increase_heading_width={increase_heading_width}
      />
      <Section ref={ref} className={`${className} ${theme?.theme || ""}`} style={styles}>
        <Figure>
          <Image {...image} sizes='100vw' style={Object.assign({}, !ssr && { opacity: 1 })} />
        </Figure>
        {sub_heading?.length ? <SubHeading theme={theme ? theme?.theme : "dark-theme"} prog={scrollY} {...sub_heading[0].heading} /> : null}
        {!no_overlay && <Overlay prog={scrollY} doubled={doubled} />}
        {blocks?.has("cta_block") ? (
          <BlockCTA {...blocks?.get("cta_block")} prog={scrollY} doubled={doubled} />
        ) : blocks?.has("skyline") ? (
          <Skyline
            theme={secundary_theme ? secundary_theme?.theme : "dark-theme"}
            {...blocks?.get("skyline")}
            isHeading={heading?.length}
            prog={scrollY}
            doubled={doubled}
          />
        ) : null}
      </Section>
    </>
  )
}
